/** Handles disponibles: con proporción fija NO existen los laterales (§5.5). */
export function handlesDe(restricciones) {
    return restricciones.proporcionFija
        ? ['nw', 'ne', 'sw', 'se']
        : ['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'];
}
/** Limita un rect (en %) al lienzo y a los tamaños mín/máx del elemento. */
export function limitarRect(rect, restricciones) {
    const salida = { ...rect };
    const min = restricciones.tamanoMin;
    const max = restricciones.tamanoMax;
    salida.w = Math.min(Math.max(salida.w, min), max);
    salida.h = Math.min(Math.max(salida.h, min), max);
    if (restricciones.dentroDelLienzo) {
        salida.w = Math.min(salida.w, 100);
        salida.h = Math.min(salida.h, 100);
        salida.x = Math.min(Math.max(salida.x, 0), 100 - salida.w);
        salida.y = Math.min(Math.max(salida.y, 0), 100 - salida.h);
    }
    return salida;
}
export function moverRect(rect, dxPct, dyPct, restricciones) {
    return limitarRect({ ...rect, x: rect.x + dxPct, y: rect.y + dyPct }, restricciones);
}
/**
 * Redimensiona desde un handle. Con `proporcionFija` las esquinas escalan proporcional
 * — jamás estiran (regla 11). dx/dy en % del lienzo.
 */
export function redimensionarRect(rect, handle, dxPct, dyPct, restricciones, lienzo) {
    let { x, y, w, h } = rect;
    const derecha = handle.includes('e');
    const izquierda = handle.includes('w');
    const abajo = handle.includes('s');
    const arriba = handle.includes('n');
    if (restricciones.proporcionFija && handle.length === 2) {
        // Escala proporcional guiada por el eje dominante, en píxeles reales del lienzo.
        const razon = (rect.w * lienzo.w) / (rect.h * lienzo.h);
        const dxPx = dxPct * (lienzo.w / 100) * (derecha ? 1 : -1);
        const dyPx = dyPct * (lienzo.h / 100) * (abajo ? 1 : -1);
        const deltaPx = Math.abs(dxPx) >= Math.abs(dyPx) ? dxPx : dyPx * razon;
        const nuevoWpx = rect.w * (lienzo.w / 100) + deltaPx;
        const nuevoHpx = nuevoWpx / razon;
        w = (nuevoWpx / lienzo.w) * 100;
        h = (nuevoHpx / lienzo.h) * 100;
        if (izquierda)
            x = rect.x + rect.w - w;
        if (arriba)
            y = rect.y + rect.h - h;
    }
    else {
        if (derecha)
            w = rect.w + dxPct;
        if (izquierda) {
            w = rect.w - dxPct;
            x = rect.x + dxPct;
        }
        if (abajo)
            h = rect.h + dyPct;
        if (arriba) {
            h = rect.h - dyPct;
            y = rect.y + dyPct;
        }
    }
    if (w < 1)
        w = 1;
    if (h < 1)
        h = 1;
    return limitarRect({ x, y, w, h }, restricciones);
}
export function calcularSnap(rect, otros, umbralPct) {
    const candidatosX = [0, 50, 100];
    const candidatosY = [0, 50, 100];
    for (const o of otros) {
        candidatosX.push(o.x, o.x + o.w / 2, o.x + o.w);
        candidatosY.push(o.y, o.y + o.h / 2, o.y + o.h);
    }
    const bordesX = [rect.x, rect.x + rect.w / 2, rect.x + rect.w];
    const bordesY = [rect.y, rect.y + rect.h / 2, rect.y + rect.h];
    let mejorDx = null;
    let mejorDy = null;
    const guias = [];
    for (const candidato of candidatosX) {
        for (const borde of bordesX) {
            const delta = candidato - borde;
            if (Math.abs(delta) <= umbralPct && (mejorDx === null || Math.abs(delta) < Math.abs(mejorDx))) {
                mejorDx = delta;
            }
        }
    }
    for (const candidato of candidatosY) {
        for (const borde of bordesY) {
            const delta = candidato - borde;
            if (Math.abs(delta) <= umbralPct && (mejorDy === null || Math.abs(delta) < Math.abs(mejorDy))) {
                mejorDy = delta;
            }
        }
    }
    if (mejorDx !== null)
        guias.push({ eje: 'x', pos: rect.x + mejorDx });
    if (mejorDy !== null)
        guias.push({ eje: 'y', pos: rect.y + mejorDy });
    return { dx: mejorDx ?? 0, dy: mejorDy ?? 0, guias };
}
/** Rects de los demás elementos visibles (para el snapping). */
export function rectsDeOtros(elementos, idActivo) {
    return elementos.filter((e) => e.id !== idActivo && e.visible).map((e) => e.rect);
}
/** Alinear un rect dentro de un marco (por defecto, el lienzo completo). */
export function alinear(rect, eje, marco = { x: 0, y: 0, w: 100, h: 100 }) {
    const salida = { ...rect };
    switch (eje) {
        case 'izquierda':
            salida.x = marco.x;
            break;
        case 'centroH':
            salida.x = marco.x + marco.w / 2 - rect.w / 2;
            break;
        case 'derecha':
            salida.x = marco.x + marco.w - rect.w;
            break;
        case 'arriba':
            salida.y = marco.y;
            break;
        case 'centroV':
            salida.y = marco.y + marco.h / 2 - rect.h / 2;
            break;
        case 'abajo':
            salida.y = marco.y + marco.h - rect.h;
            break;
    }
    return salida;
}
function cajaDe(rects) {
    const x = Math.min(...rects.map((r) => r.x));
    const y = Math.min(...rects.map((r) => r.y));
    return {
        x,
        y,
        w: Math.max(...rects.map((r) => r.x + r.w)) - x,
        h: Math.max(...rects.map((r) => r.y + r.h)) - y,
    };
}
/**
 * Alineación de la selección (§5.3): con UN elemento alinea contra el lienzo; con
 * varios, contra el bounding box de la propia selección (comportamiento de Figma).
 */
export function alinearElementos(elementos, ids, eje) {
    const seleccion = elementos.filter((e) => ids.has(e.id));
    if (seleccion.length === 0)
        return elementos;
    const marco = seleccion.length === 1 ? undefined : cajaDe(seleccion.map((e) => e.rect));
    return elementos.map((e) => ids.has(e.id) ? { ...e, rect: alinear(e.rect, eje, marco) } : e);
}
/** Distribución con espaciado uniforme entre los elementos seleccionados (mínimo 3). */
export function distribuirElementos(elementos, ids, eje) {
    const seleccion = elementos.filter((e) => ids.has(e.id));
    if (seleccion.length < 3)
        return elementos;
    const pos = eje === 'horizontal' ? 'x' : 'y';
    const dim = eje === 'horizontal' ? 'w' : 'h';
    const ordenados = [...seleccion].sort((a, b) => a.rect[pos] - b.rect[pos]);
    const inicio = ordenados[0].rect[pos];
    const fin = ordenados.at(-1).rect[pos] + ordenados.at(-1).rect[dim];
    const ocupado = ordenados.reduce((acc, e) => acc + e.rect[dim], 0);
    const hueco = (fin - inicio - ocupado) / (ordenados.length - 1);
    const nuevasPos = new Map();
    let cursor = inicio;
    for (const e of ordenados) {
        nuevasPos.set(e.id, cursor);
        cursor += e.rect[dim] + hueco;
    }
    return elementos.map((e) => nuevasPos.has(e.id) ? { ...e, rect: { ...e.rect, [pos]: nuevasPos.get(e.id) } } : e);
}
/**
 * D21: las fotos de producto son CUADRADAS, así que el rect del elemento `producto`
 * también lo es EN PÍXELES (w% del ancho == h% del alto). Si no, el bounding box sale
 * rectangular alrededor de una foto cuadrada.
 */
export function cuadrarRect(rect, lienzo) {
    const ladoPx = (rect.w / 100) * lienzo.w;
    let h = (ladoPx / lienzo.h) * 100;
    let w = rect.w;
    if (rect.y + h > 100) {
        // no cabe: se reduce el lado manteniendo el cuadrado
        h = 100 - rect.y;
        w = ((h / 100) * lienzo.h / lienzo.w) * 100;
    }
    return { ...rect, w, h };
}
/** Normaliza todos los elementos `producto` de un lienzo a cuadrado (idempotente). */
export function cuadrarProductos(elementos, lienzo) {
    return elementos.map((e) => {
        if (e.tipo !== 'producto')
            return e;
        const cuadrado = cuadrarRect(e.rect, lienzo);
        return Math.abs(cuadrado.h - e.rect.h) < 0.01 && Math.abs(cuadrado.w - e.rect.w) < 0.01
            ? e
            : { ...e, rect: cuadrado };
    });
}
//# sourceMappingURL=geometria.js.map